import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import { defaultConfig } from './index'

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

// No existe SelectField compartido en components/Indicators: select local con
// el mismo estilo de los inputs del panel.
function SelectRow({ label, value, options, onChange }) {
  return (
    <div className="space-y-1">
      <span className="text-xs text-[var(--ink-soft,#4A5A52)]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1 text-[11px] rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-white/80 dark:bg-gray-900/50 text-[var(--ink,#15211B)]"
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

const STYLE_KEYS = ['colorBull', 'colorBear', 'colorAccent', 'colorNeutral']

export default function BootPullbackPanel({ config, onChange, onClose }) {
  const cfg = config || {}

  const update = (key, value) => onChange({ ...cfg, [key]: value })
  const resetStyle = () => {
    const patch = {}
    for (const k of STYLE_KEYS) patch[k] = defaultConfig[k]
    onChange({ ...cfg, ...patch })
  }

  return (
    <IndicatorConfigModal title="Configuración Boot Pullback" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">

        <Section title="Lógica" sectionKey="logic" config={cfg} onChange={onChange}>
          <ToggleField label="Trigger por mecha" value={cfg.triggerOnWick ?? true} onChange={(v) => update('triggerOnWick', v)} />
          <SliderField label="Pivote interno (barras)" value={cfg.internalPivotLen ?? 2} min={1} max={5} step={1} onChange={(v) => update('internalPivotLen', v)} />
        </Section>

        <Section title="Estructura externa" sectionKey="external" config={cfg} onChange={onChange}>
          <ToggleField label="Zigzag externo" value={cfg.showExternalStructure ?? true} onChange={(v) => update('showExternalStructure', v)} />
          <ToggleField label="Niveles Ext H/L" value={cfg.showExtLevels ?? true} onChange={(v) => update('showExtLevels', v)} />
          <ToggleField label="Ext Mid" value={cfg.showExtMid ?? true} onChange={(v) => update('showExtMid', v)} />
          <ToggleField label="Sombreado de flujo" value={cfg.showShading ?? true} onChange={(v) => update('showShading', v)} />
          <ToggleField label="Etiquetas CHoCH/BOS" value={cfg.showLabels ?? true} onChange={(v) => update('showLabels', v)} />
          <SliderField label="Offset X etiquetas" value={cfg.labelOffsetX ?? 0} min={-40} max={40} step={1} unit="px" onChange={(v) => update('labelOffsetX', v)} />
        </Section>

        <Section title="Estructura interna" sectionKey="internal" config={cfg} onChange={onChange}>
          <ToggleField label="Activar capa interna" value={cfg.showInternalStructure ?? false} onChange={(v) => update('showInternalStructure', v)} />
          <ToggleField label="Zigzag interno" value={cfg.showInternalZigzag ?? true} onChange={(v) => update('showInternalZigzag', v)} />
          <ToggleField label="Niveles Int H/L" value={cfg.showIntLevels ?? true} onChange={(v) => update('showIntLevels', v)} />
        </Section>

        <Section title="Marcadores" sectionKey="markers" config={cfg} onChange={onChange}>
          <ToggleField label="Marcadores de swing" value={cfg.showSwingMarkers ?? true} onChange={(v) => update('showSwingMarkers', v)} />
          <ToggleField label="Debug anclas" value={cfg.debugAnchors ?? false} onChange={(v) => update('debugAnchors', v)} />
        </Section>

        <Section title="Tabla de estado" sectionKey="stateTable" config={cfg} onChange={onChange}>
          <ToggleField label="Mostrar tabla" value={cfg.showStateTable ?? false} onChange={(v) => update('showStateTable', v)} />
          <SelectRow
            label="Posición"
            value={cfg.stateTablePosition ?? 'top-right'}
            options={[
              { value: 'top-left', label: 'Superior izquierda' },
              { value: 'top-right', label: 'Superior derecha' },
              { value: 'bottom-left', label: 'Inferior izquierda' },
              { value: 'bottom-right', label: 'Inferior derecha' },
            ]}
            onChange={(v) => update('stateTablePosition', v)}
          />
          <SelectRow
            label="Tamaño de texto"
            value={cfg.stateTableTextSize ?? 'small'}
            options={[
              { value: 'small', label: 'Pequeño' },
              { value: 'medium', label: 'Mediano' },
              { value: 'large', label: 'Grande' },
            ]}
            onChange={(v) => update('stateTableTextSize', v)}
          />
        </Section>

        <Section title="Multitemporal" sectionKey="mtf" config={cfg} onChange={onChange}>
          <ToggleField label="Panel multitemporal" value={cfg.showMultiTimeframe ?? false} onChange={(v) => update('showMultiTimeframe', v)} />
          <SelectRow
            label="HTF para la tabla"
            value={cfg.htfOverride ?? 'auto'}
            options={[
              { value: 'auto', label: 'Automático (TF superior)' },
              { value: '1m', label: '1m' },
              { value: '5m', label: '5m' },
              { value: '15m', label: '15m' },
              { value: '1h', label: '1h' },
              { value: '4h', label: '4h' },
              { value: '1d', label: '1D' },
              { value: '1w', label: '1W' },
              { value: '1M', label: '1M' },
            ]}
            onChange={(v) => update('htfOverride', v)}
          />
        </Section>

        <Section title="Estilo" sectionKey="style" config={cfg} onChange={onChange}>
          <ColorPickerField label="Alcista" value={cfg.colorBull ?? '#1B5E20'} onChange={(v) => update('colorBull', v)} />
          <ColorPickerField label="Bajista" value={cfg.colorBear ?? '#B71C1C'} onChange={(v) => update('colorBear', v)} />
          <ColorPickerField label="Acento (transición)" value={cfg.colorAccent ?? '#B08D57'} onChange={(v) => update('colorAccent', v)} />
          <ColorPickerField label="Neutro" value={cfg.colorNeutral ?? '#374151'} onChange={(v) => update('colorNeutral', v)} />
          <button
            onClick={resetStyle}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
          >
            <RotateCcw size={12} />
            Restablecer estilo Quantum
          </button>
        </Section>

      </div>
    </IndicatorConfigModal>
  )
}
